import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCustom, useApiUrl } from '@refinedev/core';
import { formatDate, formatDateOrNever } from '@/lib/utils';
import { PromotionStatusBadge } from '@/components/coupons/PromotionStatusBadge';
import { GrantPromotionModal } from '@/components/coupons/GrantPromotionModal';
import {
  PromotionKpis,
  ClaimsTable,
  RedemptionsTable,
  CodesSummary,
  CodesTable,
  userIdFilterProblem,
} from '@/components/coupons/PromotionPanels';
import type {
  PromotionStats,
  ClaimListRow,
  RedemptionListRow,
  ListPage,
  CodesPage,
} from '@/components/coupons/PromotionPanels';
import {
  SOURCE_LABELS,
  issuanceLabel,
  TRANSITION_DONE,
} from '@/lib/promotion-labels';
import type {
  PromotionSource,
  IssuanceType,
  PromotionStatus,
  PromotionTransition,
} from '@/lib/promotion-labels';
import { adminFetch } from '@/providers/admin-api';
import { toast } from '@/hooks/useToast';
import {
  ArrowLeft,
  Gift,
  Edit,
  Play,
  Pause,
  RotateCcw,
  Square,
  Archive,
  Download,
  Plus,
  Filter,
  Loader2,
  AlertCircle,
  X,
  BarChart2,
  Users,
  ShoppingCart,
  Code2,
  ClipboardList,
  Info,
} from 'lucide-react';

// ─── Types ──────────────────────────────────────────────────────────────────

interface Promotion {
  id: string;
  name: string;
  description: string | null;
  source: PromotionSource;
  paywall_type: string;
  coupons_per_claim: number;
  issuance_type: IssuanceType;
  shareable_code: string | null;
  max_total_claims: number | null;
  max_claims_per_user: number;
  starts_at: string;
  ends_at: string | null;
  status: PromotionStatus;
  environment: 'dev' | 'prod';
  created_at: string;
  updated_at: string;
  /** Counted by the API over every claim and redemption (#3350). */
  stats: PromotionStats;
}

// Valid transitions per status (backend services/coupons/promotions.ts transitionPromotionStatus)
const TRANSITIONS: Record<PromotionStatus, Array<{ action: PromotionTransition; label: string; icon: React.ElementType; variant: 'primary' | 'warning' | 'danger' | 'secondary' }>> = {
  draft: [
    { action: 'activate', label: 'Activate', icon: Play, variant: 'primary' },
    { action: 'archive', label: 'Archive', icon: Archive, variant: 'secondary' },
  ],
  active: [
    { action: 'pause', label: 'Pause', icon: Pause, variant: 'warning' },
    { action: 'end', label: 'End', icon: Square, variant: 'danger' },
  ],
  paused: [
    { action: 'resume', label: 'Resume', icon: RotateCcw, variant: 'primary' },
    { action: 'end', label: 'End', icon: Square, variant: 'danger' },
  ],
  ended: [
    { action: 'archive', label: 'Archive', icon: Archive, variant: 'secondary' },
  ],
  archived: [],
};

const VARIANT_CLASSES: Record<string, string> = {
  primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
  warning: 'bg-amber-500 text-white hover:bg-amber-600',
  danger: 'bg-destructive text-white hover:bg-destructive/90',
  secondary: 'border bg-background hover:bg-muted text-foreground',
};

// ─── Confirm-with-reason Modal ────────────────────────────────────────────────

interface ConfirmTransitionModalProps {
  action: string;
  promotionName: string;
  onConfirm: (reason: string) => void;
  onClose: () => void;
  loading: boolean;
}

function ConfirmTransitionModal({ action, promotionName, onConfirm, onClose, loading }: ConfirmTransitionModalProps) {
  const [reason, setReason] = useState('');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b p-5">
          <h2 className="text-lg font-semibold capitalize">{action} Promotion</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-muted-foreground">
            You are about to <strong>{action}</strong>{' '}
            <strong>{promotionName}</strong>. This action will be logged.
          </p>
          <div>
            <label className="mb-1 block text-sm font-medium">
              Reason <span className="text-destructive">*</span>
            </label>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={loading}
              placeholder="Describe why you're performing this action…"
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm disabled:opacity-50"
            />
            <p className="mt-1 text-xs text-muted-foreground">Required (≥ 3 chars).</p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t p-5">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason.trim())}
            disabled={loading || reason.trim().length < 3}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50 capitalize"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? 'Processing…' : action}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Generate Codes Modal ─────────────────────────────────────────────────────

interface GenerateCodesModalProps {
  promotionId: string;
  promotionName: string;
  onClose: () => void;
  onGenerated: () => void;
}

function GenerateCodesModal({ promotionId, promotionName, onClose, onGenerated }: GenerateCodesModalProps) {
  const apiUrl = useApiUrl();
  const [count, setCount] = useState('10');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    const n = parseInt(count, 10);
    if (!n || n < 1 || reason.trim().length < 3) return;
    setLoading(true);
    setError(null);
    try {
      const r = await adminFetch(`${apiUrl}/v1/admin/promotions/${promotionId}/codes/generate`, {
        method: 'POST',
        body: JSON.stringify({ count: n, reason: reason.trim() }),
      });
      const result = (await r.json()) as { generated: number };
      toast({ title: 'Codes generated', description: `${result.generated} new codes added to ${promotionName}.`, variant: 'success' });
      onGenerated();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const countNum = parseInt(count, 10);
  const canSubmit = !isNaN(countNum) && countNum >= 1 && reason.trim().length >= 3 && !loading;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-lg bg-card shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b p-5">
          <h2 className="flex items-center gap-2 text-lg font-semibold"><Plus className="h-5 w-5" /> Generate Codes</h2>
          <button type="button" onClick={onClose} disabled={loading} className="text-muted-foreground hover:text-foreground disabled:opacity-50"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">How many codes? <span className="text-destructive">*</span></label>
            <input
              type="number"
              min={1}
              max={10000}
              value={count}
              onChange={(e) => setCount(e.target.value)}
              disabled={loading}
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm disabled:opacity-50"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Reason <span className="text-destructive">*</span></label>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={loading}
              placeholder="e.g. Black Friday batch, influencer campaign phase 2…"
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm disabled:opacity-50"
            />
          </div>
          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive" />
              <span className="text-destructive">{error}</span>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t p-5">
          <button type="button" onClick={onClose} disabled={loading} className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50">Cancel</button>
          <button type="button" onClick={handleGenerate} disabled={!canSubmit} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? 'Generating…' : `Generate ${countNum >= 1 ? countNum : ''} codes`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Audit Tab ────────────────────────────────────────────────────────────────

interface AuditEntry {
  id: string;
  admin_user_id: string | null;
  admin_email?: string | null;
  admin_display_name?: string | null;
  action: string;
  entity_type: string;
  entity_id: string;
  details: Record<string, unknown>;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

function AuditTab({ promotionId }: { promotionId: string }) {
  const { data, isLoading, isError } = useCustom<{ entries: AuditEntry[]; total: number }>({
    url: '/v1/admin/audit',
    method: 'get',
    config: { query: { entity_id: promotionId, limit: '100' } },
  });
  const entries = data?.data?.entries ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading audit log…
      </div>
    );
  }
  if (isError) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
        <AlertCircle className="h-4 w-4" />
        Failed to load audit log.
      </div>
    );
  }
  if (entries.length === 0) {
    return (
      <div className="rounded-lg border bg-muted/30 p-8 text-center space-y-2">
        <ClipboardList className="mx-auto h-10 w-10 text-muted-foreground/60" />
        <h3 className="text-base font-medium">No audit entries yet</h3>
        <p className="text-sm text-muted-foreground">Admin actions on this promotion will appear here.</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left">
          <tr>
            <th className="px-3 py-2 font-medium">Action</th>
            <th className="px-3 py-2 font-medium">Admin</th>
            <th className="px-3 py-2 font-medium">Reason</th>
            <th className="px-3 py-2 font-medium">When</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.id} className="border-t">
              <td className="px-3 py-2">
                <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{e.action}</code>
              </td>
              <td className="px-3 py-2 text-muted-foreground">
                {e.admin_display_name ?? e.admin_email ?? (e.admin_user_id ? `${e.admin_user_id.slice(0, 8)}…` : '—')}
              </td>
              <td className="px-3 py-2 text-muted-foreground italic">
                {(e.details?.reason as string | undefined) ?? '—'}
              </td>
              <td className="px-3 py-2 text-xs text-muted-foreground">{formatDate(e.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Tab definitions ──────────────────────────────────────────────────────────

type TabId = 'overview' | 'claims' | 'redemptions' | 'codes' | 'audit';

const ALL_TABS: Array<{ id: TabId; label: string; icon: React.ElementType }> = [
  { id: 'overview', label: 'Overview', icon: BarChart2 },
  { id: 'claims', label: 'Claims', icon: Users },
  { id: 'redemptions', label: 'Redemptions', icon: ShoppingCart },
  { id: 'codes', label: 'Codes', icon: Code2 },
  { id: 'audit', label: 'Audit', icon: ClipboardList },
];

// ─── Main Component ───────────────────────────────────────────────────────────

export function PromotionShowPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiUrl = useApiUrl();

  // Tab state
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  // Transition modal
  const [transitionAction, setTransitionAction] = useState<{ action: PromotionTransition; label: string } | null>(null);
  const [transitionLoading, setTransitionLoading] = useState(false);
  // Bumped after an action, so a mounted Audit tab reloads and shows it.
  const [auditVersion, setAuditVersion] = useState(0);

  // Grant modal
  const [grantModalOpen, setGrantModalOpen] = useState(false);

  // Generate codes modal
  const [generateCodesOpen, setGenerateCodesOpen] = useState(false);

  // Claims filters
  const [claimStatusFilter, setClaimStatusFilter] = useState('');
  const [claimUserFilter, setClaimUserFilter] = useState('');
  const claimUserProblem = userIdFilterProblem(claimUserFilter);

  // Redemptions filters
  const [redemptionStatusFilter, setRedemptionStatusFilter] = useState('');

  // ── Data fetching ──
  // Each query is a useCustom, keyed by its URL; refresh one with its own
  // refetch(). useInvalidate({ resource: 'promotions' }) does NOT reach these
  // keys, which is why the header kept a stale status after an action (#3350).

  const {
    data: promotionData,
    isLoading: promotionLoading,
    isError: promotionError,
    refetch: refetchPromotion,
  } = useCustom<Promotion>({
    url: `/v1/admin/promotions/${id}`,
    method: 'get',
  });
  const promotion = promotionData?.data;

  const claimQuery: Record<string, string> = { limit: '200' };
  if (claimStatusFilter) claimQuery.status = claimStatusFilter;
  if (claimUserFilter.trim() && !claimUserProblem) claimQuery.user_id = claimUserFilter.trim();

  const claims = useCustom<ListPage<ClaimListRow>>({
    url: `/v1/admin/promotions/${id}/claims`,
    method: 'get',
    config: { query: claimQuery },
    queryOptions: { enabled: activeTab === 'claims' && !claimUserProblem },
  });

  const redemptionQuery: Record<string, string> = { limit: '200' };
  if (redemptionStatusFilter) redemptionQuery.status = redemptionStatusFilter;

  const redemptions = useCustom<ListPage<RedemptionListRow>>({
    url: `/v1/admin/promotions/${id}/redemptions`,
    method: 'get',
    config: { query: redemptionQuery },
    queryOptions: { enabled: activeTab === 'redemptions' },
  });

  const codes = useCustom<CodesPage>({
    url: `/v1/admin/promotions/${id}/codes`,
    method: 'get',
    config: { query: { limit: '500' } },
    queryOptions: { enabled: activeTab === 'codes' && promotion?.issuance_type === 'unique_codes' },
  });

  // ── Transition action ──

  const handleTransition = useCallback(async (reason: string) => {
    if (!transitionAction || !id) return;
    setTransitionLoading(true);
    try {
      await adminFetch(`${apiUrl}/v1/admin/promotions/${id}/${transitionAction.action}`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      toast({ title: 'Done', description: `Promotion ${TRANSITION_DONE[transitionAction.action]}.`, variant: 'success' });
      setTransitionAction(null);
      await refetchPromotion();
      setAuditVersion((v) => v + 1);
    } catch (err: unknown) {
      toast({ title: 'Action failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    } finally {
      setTransitionLoading(false);
    }
  }, [transitionAction, id, apiUrl, refetchPromotion]);

  // ── CSV export ──

  const handleCsvExport = async () => {
    if (!promotion || !id) return;
    try {
      const r = await adminFetch(`${apiUrl}/v1/admin/promotions/${id}/codes.csv`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${promotion.name}-codes.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      toast({ title: 'Export failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    }
  };

  const tabs = promotion?.issuance_type === 'unique_codes'
    ? ALL_TABS
    : ALL_TABS.filter((t) => t.id !== 'codes');

  // ── Render ──

  if (promotionLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (promotionError || !promotion) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 rounded-lg border border-destructive bg-destructive/10 p-4 text-destructive">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          Failed to load promotion. It may not exist or you lack access.
        </div>
      </div>
    );
  }

  const transitions = TRANSITIONS[promotion.status] ?? [];
  const canGrant = promotion.status === 'active';

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-start gap-4">
        <button
          type="button"
          onClick={() => navigate('/promotions')}
          className="mt-1 text-muted-foreground hover:text-foreground transition"
          aria-label="Back to promotions"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold flex items-center gap-2 truncate">
              <Gift className="h-6 w-6 flex-shrink-0" />
              {promotion.name}
            </h1>
            <PromotionStatusBadge status={promotion.status} />
          </div>
          {promotion.description && (
            <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{promotion.description}</p>
          )}
          <p className="mt-1 text-xs text-muted-foreground">
            ID: <code className="font-mono">{promotion.id}</code> · {promotion.environment.toUpperCase()} · Updated {formatDate(promotion.updated_at)}
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Edit — only when editable */}
          {(promotion.status === 'draft' || promotion.status === 'active') && (
            <button
              type="button"
              onClick={() => navigate(`/promotions/${promotion.id}/edit`)}
              className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-muted transition"
            >
              <Edit className="h-4 w-4" />
              Edit
            </button>
          )}
          {/* Transition buttons */}
          {transitions.map((t) => (
            <button
              key={t.action}
              type="button"
              onClick={() => setTransitionAction({ action: t.action, label: t.label })}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition ${VARIANT_CLASSES[t.variant]}`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab Nav ── */}
      <div className="border-b">
        <nav className="-mb-px flex gap-0 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:border-muted hover:text-foreground'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Tab Content ── */}
      <div className="min-h-[400px]">

        {/* ─── Overview ─── */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <PromotionKpis stats={promotion.stats} />

            {/* Config summary */}
            <div className="rounded-lg border bg-card p-6">
              <h3 className="mb-4 text-base font-semibold flex items-center gap-2"><Info className="h-4 w-4 text-muted-foreground" /> Configuration</h3>
              <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
                <div>
                  <dt className="text-muted-foreground">Source</dt>
                  <dd className="font-medium">{SOURCE_LABELS[promotion.source] ?? promotion.source}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Paywall type</dt>
                  <dd className="font-medium">{promotion.paywall_type}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Issuance type</dt>
                  <dd className="font-medium">{issuanceLabel(promotion.issuance_type)}</dd>
                </div>
                {promotion.shareable_code && (
                  <div>
                    <dt className="text-muted-foreground">Shareable code</dt>
                    <dd><code className="rounded bg-muted px-1.5 py-0.5 font-mono text-sm">{promotion.shareable_code}</code></dd>
                  </div>
                )}
                <div>
                  <dt className="text-muted-foreground">Coupons per claim</dt>
                  <dd className="font-medium">{promotion.coupons_per_claim}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Max claims per user</dt>
                  <dd className="font-medium">{promotion.max_claims_per_user}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Max total claims</dt>
                  <dd className="font-medium">{promotion.max_total_claims ?? 'Unlimited'}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Starts at</dt>
                  <dd className="font-medium">{formatDate(promotion.starts_at)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Ends at</dt>
                  <dd className="font-medium">{formatDateOrNever(promotion.ends_at)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Environment</dt>
                  <dd className="font-medium uppercase">{promotion.environment}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Created</dt>
                  <dd className="font-medium">{formatDate(promotion.created_at)}</dd>
                </div>
              </dl>
            </div>
          </div>
        )}

        {/* ─── Claims ─── */}
        {activeTab === 'claims' && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex flex-wrap items-start gap-2">
                <Filter className="mt-2.5 h-4 w-4 text-muted-foreground" />
                <select
                  value={claimStatusFilter}
                  onChange={(e) => setClaimStatusFilter(e.target.value)}
                  className="px-3 py-2 border rounded-md text-sm bg-background"
                >
                  <option value="">All statuses</option>
                  <option value="active">Active</option>
                  <option value="revoked">Revoked</option>
                  <option value="expired">Expired</option>
                </select>
                <div>
                  <input
                    type="text"
                    placeholder="Filter by user ID…"
                    value={claimUserFilter}
                    onChange={(e) => setClaimUserFilter(e.target.value)}
                    aria-invalid={claimUserProblem ? true : undefined}
                    className="w-64 rounded-md border bg-background px-3 py-2 text-sm font-mono"
                  />
                  {claimUserProblem && <p className="mt-1 text-xs text-destructive">{claimUserProblem}</p>}
                </div>
              </div>
              {canGrant ? (
                <button
                  type="button"
                  onClick={() => setGrantModalOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition"
                >
                  <Gift className="h-4 w-4" />
                  Grant to users
                </button>
              ) : (
                <p className="text-xs text-muted-foreground py-2">Only an active promotion can be granted.</p>
              )}
            </div>

            <ClaimsTable
              page={claimUserProblem ? null : claims.data?.data ?? null}
              loading={!claimUserProblem && claims.isLoading}
              error={claims.isError ? (claims.error?.message ?? 'request failed') : null}
            />
          </div>
        )}

        {/* ─── Redemptions ─── */}
        {activeTab === 'redemptions' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <select
                value={redemptionStatusFilter}
                onChange={(e) => setRedemptionStatusFilter(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm bg-background"
              >
                <option value="">All statuses</option>
                <option value="held">Held</option>
                <option value="consumed">Consumed</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>

            <RedemptionsTable
              page={redemptions.data?.data ?? null}
              loading={redemptions.isLoading}
              error={redemptions.isError ? (redemptions.error?.message ?? 'request failed') : null}
            />
          </div>
        )}

        {/* ─── Codes (unique_codes only) ─── */}
        {activeTab === 'codes' && promotion.issuance_type === 'unique_codes' && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CodesSummary page={codes.data?.data ?? null} />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCsvExport}
                  className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-muted transition"
                >
                  <Download className="h-4 w-4" />
                  Download CSV
                </button>
                <button
                  type="button"
                  onClick={() => setGenerateCodesOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition"
                >
                  <Plus className="h-4 w-4" />
                  Generate more
                </button>
              </div>
            </div>

            <CodesTable
              page={codes.data?.data ?? null}
              loading={codes.isLoading}
              error={codes.isError ? (codes.error?.message ?? 'request failed') : null}
            />
          </div>
        )}

        {/* ─── Audit ─── */}
        {activeTab === 'audit' && <AuditTab key={auditVersion} promotionId={promotion.id} />}
      </div>

      {/* ── Modals ── */}
      {transitionAction && (
        <ConfirmTransitionModal
          action={transitionAction.action}
          promotionName={promotion.name}
          onConfirm={handleTransition}
          onClose={() => setTransitionAction(null)}
          loading={transitionLoading}
        />
      )}

      {canGrant && (
        <GrantPromotionModal
          open={grantModalOpen}
          onClose={() => setGrantModalOpen(false)}
          presetPromotionId={promotion.id}
          lockPromotion={true}
          onGranted={() => {
            void claims.refetch();
            void refetchPromotion();
          }}
        />
      )}

      {generateCodesOpen && (
        <GenerateCodesModal
          promotionId={promotion.id}
          promotionName={promotion.name}
          onClose={() => setGenerateCodesOpen(false)}
          onGenerated={() => {
            void codes.refetch();
          }}
        />
      )}
    </div>
  );
}
